import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCustomer, getCustomerTransactions, addCustomerTransaction } from '../services/api';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import './CustomerDetailsPage.css';

function CustomerDetailsPage() {
  const { customerId } = useParams();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setDynamicLabel } = useBreadcrumb();

  const [customer, setCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentError, setPaymentError] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [customerData, transactionsData] = await Promise.all([
        getCustomer(customerId),
        getCustomerTransactions(customerId),
      ]);
      setCustomer(customerData);
      setTransactions(transactionsData.transactions || []);
    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
    if (customer) setDynamicLabel(customer.name);
  }, [customer, setDynamicLabel]);

  const handleRegisterPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      setPaymentError(t('customers.payment.invalidAmount'));
      return;
    }

    try {
      await addCustomerTransaction(customerId, {
        amount: parseFloat(paymentAmount),
        type: 'payment',
        description: t('customers.payment.manualPayment'),
      });
      setPaymentModalOpen(false);
      setPaymentAmount('');
      loadData(); // Reload to update balance and history
    } catch (error) {
      console.error('Error registering payment:', error);
      setPaymentError(t('customers.payment.error'));
    }
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;
  if (!customer) return <div className="error">{t('customers.notFound')}</div>;

  return (
    <div className="customer-details-page">
      <div className="page-header">
        <h1>{customer.name}</h1>
      </div>

      <div className="customer-info-card card">
        <div className="info-row">
          <div className="info-item">
            <label>{t('customers.fields.phone')}</label>
            <span>{customer.phone || '-'}</span>
          </div>
          <div className="info-item">
            <label>{t('customers.fields.email')}</label>
            <span>{customer.email || '-'}</span>
          </div>
          <div className="info-item balance-item">
            <label>{t('customers.balance')}</label>
            <span className={`balance-amount ${customer.balance > 0 ? 'debt' : 'credit'}`}>
              ${(customer.balance || 0).toFixed(2)}
            </span>
          </div>
        </div>
        <div className="actions-row">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setPaymentModalOpen(true)}
            disabled={!customer.balance || customer.balance <= 0}
          >
            {t('customers.registerPayment')}
          </button>
        </div>
      </div>

      <div className="transactions-section card">
        <h3>{t('customers.transactions.title')}</h3>
        <div className="table-responsive">
          <table className="transactions-table">
            <thead>
              <tr>
                <th>{t('common.date')}</th>
                <th>{t('common.description')}</th>
                <th>{t('customers.transactions.type')}</th>
                <th>{t('common.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((trans) => (
                <tr key={trans.SK}>
                  <td>{new Date(trans.createdAt).toLocaleString()}</td>
                  <td>{trans.description || '-'}</td>
                  <td>
                    <span className={`type-badge ${trans.type}`}>
                      {t(`customers.transactions.types.${trans.type}`)}
                    </span>
                  </td>
                  <td className={trans.type === 'payment' ? 'amount-payment' : 'amount-debt'}>
                    {trans.type === 'payment' ? '-' : '+'}${trans.amount.toFixed(2)}
                  </td>
                </tr>
              ))}
              {transactions.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center">
                    {t('customers.transactions.noTransactions')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Modal */}
      {paymentModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{t('customers.registerPayment')}</h3>
            <div className="form-group">
              <label>{t('customers.payment.amount')}</label>
              <input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder={customer.balance ? customer.balance.toFixed(2) : '0.00'}
                max={customer.balance}
              />
            </div>
            {paymentError && <p className="error-text">{paymentError}</p>}
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPaymentModalOpen(false)}
              >
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-primary" onClick={handleRegisterPayment}>
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerDetailsPage;
